<template>
  <div
    class="education-layout"
    :class="{ 'assistant-collapsed': assistantCollapsed, 'sidebar-collapsed': sidebarCollapsed }"
  >
    <AiEducationSidebar
      :collapsed="sidebarCollapsed"
      :items="resolvedMenuItems"
      @toggle="sidebarCollapsed = !sidebarCollapsed"
    />

    <div class="education-workspace">
      <AiEducationTopbar
        :assistant-collapsed="assistantCollapsed"
        @toggle-assistant="assistantCollapsed = !assistantCollapsed"
      />
      <main class="education-content">
        <slot />
      </main>
    </div>

    <AiEducationAssistant
      :collapsed="assistantCollapsed"
      @toggle="assistantCollapsed = !assistantCollapsed"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AiEducationAssistant from './AiEducationAssistant.vue'
import AiEducationSidebar from './AiEducationSidebar.vue'
import AiEducationTopbar from './AiEducationTopbar.vue'
import { buildEducationMenuItems } from '../composables/useAdminEducationMenu'
import { useEducationWorkspace } from '../composables/useEducationWorkspace'
import type { EducationMenuItem } from './types'

defineOptions({ name: 'AiEducationLayout' })

const props = defineProps<{ menuItems?: EducationMenuItem[] }>()

const sidebarCollapsed = ref(false)
const assistantCollapsed = ref(false)
const { workspace, organizations, initialize } = useEducationWorkspace()

const resolvedMenuItems = computed(() => {
  // 角色页（学生/家长等）可传入专属菜单，覆盖默认组装
  if (props.menuItems?.length) return props.menuItems

  const ws = workspace.value
  const orgWorkspaces = organizations.value.map((item) => item.workspace).filter(Boolean)
  const isPlatformAdmin =
    ws?.workspace === 'platformAdmin' ||
    (ws?.roles || []).some((r) => r === 'ai_education_platform_admin' || r === 'ai_education_super_admin')

  // 教务菜单：教师工作台不展示基础配置/学期校历等，避免越权与 403
  const isTeacherWorkspace =
    ws?.workspace === 'teacher' ||
    (orgWorkspaces.length > 0 && orgWorkspaces.every((w) => w === 'teacher'))
  const showAdminMenus =
    !isTeacherWorkspace &&
    (isPlatformAdmin ||
      (ws?.permissions || []).some(
        (p) =>
          p.startsWith('ai-education:school:') ||
          p.startsWith('ai-education:term:') ||
          p.startsWith('ai-education:overview:') ||
          p.startsWith('ai-education:course-package:')
      ))

  const currentOrg =
    ws?.organizations?.find((o) => o.membershipId === ws.membershipId) ||
    ws?.organizations?.find((o) => o.organizationId === ws.organizationId) ||
    ws?.organizations?.[0]

  return buildEducationMenuItems({
    availableWorkspaces: orgWorkspaces.length ? orgWorkspaces : ws?.workspace ? [ws.workspace] : [],
    currentWorkspace: ws?.workspace,
    isPlatformAdmin,
    showAdminMenus,
    showTeacherMenus: isTeacherWorkspace,
    eduStage: currentOrg?.eduStage || (ws as any)?.eduStage,
    parentEnabled: currentOrg?.parentEnabled
  })
})

onMounted(() => {
  initialize().catch(() => undefined)
})
</script>

<style scoped>
.education-layout {
  --education-sidebar-width: 200px;
  --education-assistant-width: 320px;
  display: grid;
  grid-template-columns: var(--education-sidebar-width) minmax(0, 1fr) var(--education-assistant-width);
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 100vh;
  overflow: hidden;
  color: #17222d;
  background: #f0f2f5;
}

.education-layout.sidebar-collapsed {
  --education-sidebar-width: 64px;
}

.education-layout.assistant-collapsed {
  --education-assistant-width: 48px;
}

.education-workspace {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.education-content {
  min-width: 0;
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 20px;
}

@media (max-width: 1279px) {
  .education-layout {
    --education-assistant-width: 48px;
  }
}

@media (max-width: 767px) {
  .education-layout {
    --education-sidebar-width: 64px;
  }

  .education-content {
    padding: 12px;
  }
}
</style>
